/**
 * アカウント関連のQuery Hooks
 * TanStack Queryを使用したデータフェッチングとキャッシュ管理
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthHeader } from '@/hooks/useAuth';
import {
  getSimpleAccountsAuthenticated,
  getSimpleAccountByIdAuthenticated,
  addAccountToFavorites,
  removeAccountFromFavorites,
} from '@/lib/api/accounts';
import { accountKeys, favoriteKeys } from '../keys';
import { CACHE_TIME } from '../client';
import type { SimpleAccount } from '@/lib/types';

/**
 * アカウント一覧を取得するQuery Hook
 * @param search 検索キーワード（オプション）
 */
export function useAccounts(search?: string) {
  const { getAuthHeader, isLoading: authHeaderLoading, isAuthorized } = useAuthHeader();

  return useQuery({
    queryKey: accountKeys.list(search),
    queryFn: async () => {
      const authHeaders = getAuthHeader();
      return getSimpleAccountsAuthenticated(authHeaders, { search });
    },
    enabled: !authHeaderLoading && isAuthorized, // 認証状態が準備完了してから実行
    staleTime: CACHE_TIME.ACCOUNTS_LIST,
  });
}

/**
 * アカウント詳細を取得するQuery Hook
 * @param id アカウントID
 */
export function useAccount(id: string) {
  const { getAuthHeader, isLoading: authHeaderLoading, isAuthorized } = useAuthHeader();

  return useQuery({
    queryKey: accountKeys.detail(id),
    queryFn: async () => {
      const authHeaders = getAuthHeader();
      return getSimpleAccountByIdAuthenticated(id, authHeaders);
    },
    enabled: !authHeaderLoading && isAuthorized && !!id, // 認証状態が準備完了し、IDが存在する場合のみ実行
    staleTime: CACHE_TIME.ACCOUNT_DETAIL,
  });
}

/**
 * お気に入り追加/削除のMutation Hook
 */
export function useToggleFavorite() {
  const queryClient = useQueryClient();
  const { getAuthHeader } = useAuthHeader();

  return useMutation({
    mutationFn: async ({
      accountId,
      shouldBeFavorite,
    }: {
      accountId: string;
      shouldBeFavorite: boolean;
    }) => {
      const authHeaders = getAuthHeader();
      if (shouldBeFavorite) {
        await addAccountToFavorites(accountId, authHeaders);
      } else {
        await removeAccountFromFavorites(accountId, authHeaders);
      }
    },
    // お気に入り一覧のキャッシュを無効化して再取得
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: favoriteKeys.all() });
    },
  });
}
